<script setup>
import { defineProps, ref, onMounted, computed, watch } from 'vue';

const props = defineProps({
    value: {
        type: Number,
        required: true
    },
    roundedSize: {
        type: String,
        default: 'rounded-md'
    },
    orientation: {
        type: String,
        default: 'vertical',
        validator: (value) => ['vertical', 'horizontal'].includes(value)
    },
    label: {
        type: String,
        default: ''
    },
    labelRotation: {
        type: [String, Number],
        default: 0
    },
    textSize: {
        type: String,
        default: 'text-sm'
    },
    thickness: {
        type: String,
        default: 'w-5' // Default for vertical (width), for horizontal it will be height
    },
    length: {
        type: String,
        default: 'h-32' // Default for vertical (height), for horizontal it will be width
    },
    labelFontWeight: {
        type: String,
        default: 'font-semibold'
    },
    animated: {
        type: Boolean,
        default: false
    },
    background: {
        type: String,
        default: ''
    }
});

const normalizedValue = computed(() => {
    return props.value > 1 ? Math.min(1, props.value / 100) : Math.max(0, Math.min(1, props.value));
});

const displayValue = ref(props.animated ? 0 : normalizedValue.value);

onMounted(() => {
    if (props.animated) {
        setTimeout(() => {
            displayValue.value = normalizedValue.value;
        }, 100);
    }
});

watch(() => props.value, (newValue) => {
    const normalized = newValue > 1 ? Math.min(1, newValue / 100) : Math.max(0, Math.min(1, newValue));
    displayValue.value = normalized;
});

const interpolateColor = (color1, color2, factor) => {
    return color1.map((channel, i) => 
        Math.round(channel + factor * (color2[i] - channel))
    );
};

const getGradientDirection = (orientation) => {
    return orientation === 'vertical' ? 'to top' : 'to right';
};

const getScoreGradient = (value, orientation) => {
    
    const normalized = typeof value === 'number' && !isNaN(value) 
        ? (value > 1 ? Math.min(1, value / 100) : Math.max(0, Math.min(1, value)))
        : 0;
    const percentage = normalized * 100;
    
    const gradientDirection = getGradientDirection(orientation);
    
    
    const red = [255, 40, 40];
    const orange = [255, 76, 0];
    const yellowGreen = [217, 255, 0];
    const mediumGreen = [34, 150, 34];
    const brightGreen = [51, 255, 0];
    
    let startColor, midColor, endColor;
    
    if (percentage <= 25) {
        const factor = Math.max(0, Math.min(1, percentage / 25));
        startColor = red;
        midColor = interpolateColor(red, orange, factor * 0.5);
        endColor = interpolateColor(red, orange, factor);
    } else if (percentage <= 50) {
        const factor = Math.max(0, Math.min(1, (percentage - 25) / 25));
        startColor = orange;
        midColor = interpolateColor(orange, yellowGreen, factor * 0.5);
        endColor = interpolateColor(orange, yellowGreen, factor);
    } else if (percentage <= 80) {
        const factor = Math.max(0, Math.min(1, (percentage - 50) / 25));
        startColor = yellowGreen;
        midColor = interpolateColor(yellowGreen, mediumGreen, factor * 0.5);
        endColor = interpolateColor(yellowGreen, mediumGreen, factor);
    } else {
        const factor = Math.max(0, Math.min(1, (percentage - 75) / 25));
        startColor = mediumGreen;
        midColor = interpolateColor(mediumGreen, brightGreen, factor * 0.5);
        endColor = interpolateColor(mediumGreen, brightGreen, factor);
    }
    
    const midMidColor = interpolateColor(midColor, endColor, 0.5);
    
    return `linear-gradient(${gradientDirection}, 
        rgb(${startColor.join(', ')}), 
        rgb(${midColor.join(', ')}) 35%, 
        rgb(${midMidColor.join(', ')}) 65%,
        rgb(${endColor.join(', ')})
    )`;
};

</script>

<template>
    <div 
        class="flex items-center gap-1"
        :class="{
            'flex-row items-end': props.orientation === 'vertical',
            'flex-row items-center': props.orientation === 'horizontal'
        }"
    >
        <div 
            v-if="label && props.orientation === 'vertical'"
            class="relative flex items-end justify-center h-full w-4"
        >
            <span 
                class="text-white whitespace-nowrap tracking-wider transition-all duration-300 block absolute bottom-2 origin-bottom-right"
                :class="[textSize, labelFontWeight]"
                :style="{ 
                    transform: 'rotate(-90deg) translateX(100%)',
                    right: '0',
                    width: 'max-content'
                }"
            >
                {{ label }}
            </span>
        </div>

        <!-- Alignment Wrapper (Define max space) -->
        <div 
            class="flex"
            :class="[
                props.orientation === 'vertical' ? `flex-col justify-end ${length}` : `${length} items-center`,
                background && background.startsWith('bg-') ? `${roundedSize} overflow-hidden ${background}` : (background ? `${roundedSize} overflow-hidden` : '')
            ]"
            :style="background && !background.startsWith('bg-') ? { background } : {}"
        >
            <!-- Bar Container (Dynamic Size + Border) -->
            <div 
                class="flex overflow-hidden relative transition-all duration-1000 ease-out"
                :class="[
                    roundedSize,
                    thickness
                ]"
                :style="{ 
                    [props.orientation === 'vertical' ? 'height' : 'width']: `${Math.min(100, Math.max(0, displayValue * 100))}%`,
                    background: `linear-gradient(${getGradientDirection(props.orientation)}, #666666, #FFFFFF)`,
                    padding: '2px'
                }"
            >
                <!-- Inner container with gradient fill -->
                <div 
                    class="w-full h-full relative"
                    :class="roundedSize"
                    style="overflow: hidden;"
                >
                    <!-- Filled Bar -->
                    <div 
                        class="w-full h-full absolute top-0 left-0"
                        :style="{ 
                            background: getScoreGradient(normalizedValue, props.orientation)
                        }"
                    ></div>
                </div>
            </div>
        </div>

        <!-- Label (Right side for horizontal only) -->
        <span 
            v-if="label && props.orientation === 'horizontal'"
            class="text-white whitespace-nowrap transition-all duration-300 block"
            :class="[textSize, labelFontWeight]"
        >
            {{ label }}
        </span>
    </div>
</template>
